import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    User, CreditCard as CreditCardIcon, CheckCircle2, LogOut, ShieldAlert,
    Mail, UserCircle, Building2, ExternalLink, Loader2, Users, FileText,
    PartyPopper, X, Bell, Globe, ChevronRight, Zap, ShieldCheck, Banknote,
    AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { getPlanLimits, formatLimit, usagePct, type Plan } from '../lib/plans';

/** Emails allowed to use the dev plan switcher (bypasses Stripe). */
const ADMIN_EMAILS = new Set<string>(['ruzovykiblik@gmail.com']);

const BASIC_PRICE_ID           = import.meta.env.VITE_STRIPE_BASIC_PRICE_ID as string;
const PRO_PRICE_ID             = import.meta.env.VITE_STRIPE_PRO_PRICE_ID as string;
const BUSINESS_PRICE_ID        = import.meta.env.VITE_STRIPE_BUSINESS_PRICE_ID as string;
const BASIC_ANNUAL_PRICE_ID    = import.meta.env.VITE_STRIPE_BASIC_ANNUAL_PRICE_ID as string;
const PRO_ANNUAL_PRICE_ID      = import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID as string;
const BUSINESS_ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_BUSINESS_ANNUAL_PRICE_ID as string;

/**
 * Authenticated POST to one of our /api/stripe-* endpoints. The server uses
 * the Bearer token to identify the user — the body's userId is verified
 * against the token, so we always send both.
 */
async function callApi(endpoint: string, body: object) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
}

const ACCENT       = '#16a34a';
const ACCENT_LIGHT = 'rgba(22,163,74,0.10)';

const card: React.CSSProperties = {
    background: '#fff', borderRadius: '20px', padding: '1.75rem',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 4px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.03)',
};

const iconBox = (bg: string, color: string): React.CSSProperties => ({
    background: bg, padding: '0.625rem', borderRadius: '12px',
    color, display: 'flex', flexShrink: 0,
});

const fieldLabel: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', color: '#94a3b8', marginBottom: '7px',
};

const inputBase: React.CSSProperties = {
    width: '100%', padding: '0.75rem 1rem', borderRadius: '12px',
    border: '1.5px solid #e8ecf0', background: '#f8fafc',
    fontSize: '0.9375rem', fontWeight: 500, color: '#1a1d2e',
    outline: 'none', boxSizing: 'border-box' as const,
};

export default function SettingsPage() {
    const { user, logout, updateProfile } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loadingPlan, setLoadingPlan]     = useState<string | null>(null);
    const [usage, setUsage]                 = useState({ properties: 0, tenants: 0, documents: 0 });
    const [billing, setBilling]             = useState<'monthly' | 'annual'>('monthly');
    const [notifs, setNotifs]               = useState({ weekly: true, overdue: true, maintenance: true });
    const [saving, setSaving]               = useState(false);
    const [nameValue, setNameValue]         = useState(user.name);
    const [adminSwitching, setAdminSwitching] = useState<Plan | null>(null);
    const [exportingData, setExportingData] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletePhrase, setDeletePhrase] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [connect, setConnect] = useState<{
        loading: boolean;
        connected: boolean;
        chargesEnabled: boolean;
        detailsSubmitted: boolean;
        action: 'idle' | 'onboarding' | 'refreshing';
    }>({ loading: true, connected: false, chargesEnabled: false, detailsSubmitted: false, action: 'idle' });
    const justUpgraded = searchParams.get('upgraded') === 'true';
    const justConnected = searchParams.get('connect') === 'done';
    const isAdmin = ADMIN_EMAILS.has(user.email);

    const refreshConnectStatus = async () => {
        if (!user.id) return;
        try {
            const data = await callApi('/api/stripe-connect-status', { userId: user.id });
            setConnect({
                loading: false,
                connected: Boolean(data.connected),
                chargesEnabled: Boolean(data.chargesEnabled),
                detailsSubmitted: Boolean(data.detailsSubmitted),
                action: 'idle',
            });
        } catch (err) {
            console.error('[connect-status]', err);
            setConnect(c => ({ ...c, loading: false, action: 'idle' }));
        }
    };

    const handleConnectStripe = async () => {
        if (!user.id) return;
        setConnect(c => ({ ...c, action: 'onboarding' }));
        try {
            const { url } = await callApi('/api/stripe-connect-onboard', {
                userId: user.id,
                userEmail: user.email,
            });
            window.location.href = url;
        } catch (err: any) {
            alert('Could not start Stripe onboarding: ' + err.message);
            setConnect(c => ({ ...c, action: 'idle' }));
        }
    };

    const dismissConnectBanner = () => {
        searchParams.delete('connect');
        setSearchParams(searchParams, { replace: true });
    };

    const adminSetPlan = async (plan: Plan) => {
        if (!user.id) return;
        setAdminSwitching(plan);
        const { error } = await supabase
            .from('profiles')
            .update({
                plan,
                subscription_status: plan === 'Free' ? null : 'active',
            })
            .eq('id', user.id);
        if (error) {
            alert('Admin switch failed: ' + error.message);
            setAdminSwitching(null);
            return;
        }
        window.location.reload();
    };

    const dismissUpgradeBanner = () => {
        searchParams.delete('upgraded');
        setSearchParams(searchParams, { replace: true });
    };

    useEffect(() => {
        if (user.name) setNameValue(user.name);
    }, [user.name]);

    useEffect(() => {
        if (!user.id) return;
        refreshConnectStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    useEffect(() => {
        if (!user.id) return;
        (async () => {
            const [{ count: propCount }, { data: propIds }, { count: docCount }] = await Promise.all([
                supabase.from('properties').select('*', { count: 'exact', head: true }).eq('owner_id', user.id),
                supabase.from('properties').select('id').eq('owner_id', user.id),
                supabase.from('documents').select('*', { count: 'exact', head: true }).eq('owner_id', user.id),
            ]);
            let tenantCount = 0;
            if (propIds && propIds.length > 0) {
                const { count } = await supabase
                    .from('tenant_links').select('*', { count: 'exact', head: true })
                    .in('property_id', propIds.map(p => p.id));
                tenantCount = count || 0;
            }
            setUsage({ properties: propCount || 0, tenants: tenantCount, documents: docCount || 0 });
        })();
    }, [user.id]);

    const handleUpgrade = async (planLabel: string) => {
        const ids: Record<string, { monthly: string; annual: string }> = {
            Basic:    { monthly: BASIC_PRICE_ID,    annual: BASIC_ANNUAL_PRICE_ID },
            Pro:      { monthly: PRO_PRICE_ID,      annual: PRO_ANNUAL_PRICE_ID },
            Business: { monthly: BUSINESS_PRICE_ID, annual: BUSINESS_ANNUAL_PRICE_ID },
        };
        const priceId = billing === 'annual' ? ids[planLabel]?.annual : ids[planLabel]?.monthly;
        if (!priceId) { alert('Stripe price ID not configured.'); return; }
        setLoadingPlan(planLabel);
        try {
            const { url } = await callApi('/api/create-checkout-session', { priceId, userId: user.id, userEmail: user.email });
            window.location.href = url;
        } catch (err: any) {
            alert('Could not start checkout: ' + err.message);
            setLoadingPlan(null);
        }
    };

    const handleManageBilling = async () => {
        setLoadingPlan('portal');
        try {
            const { url } = await callApi('/api/create-portal-session', { userId: user.id });
            window.location.href = url;
        } catch (err: any) {
            alert('Could not open billing portal: ' + err.message);
            setLoadingPlan(null);
        }
    };

    /**
     * GDPR Art. 15/20 — call the export endpoint and stream the response into
     * an `<a download>` so the user gets a single JSON file with their data.
     * The endpoint uses the bearer token to identify the user; nothing else.
     */
    const handleExportData = async () => {
        setExportingData(true);
        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            const res = await fetch('/api/gdpr-export', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken ? `Bearer ${accessToken}` : '',
                },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(err.error || `Export failed (HTTP ${res.status})`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `rentnova-export-${Date.now()}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            alert('Export failed: ' + err.message);
        } finally {
            setExportingData(false);
        }
    };

    /**
     * GDPR Art. 17 — confirm phrase, then call delete endpoint, then sign out.
     * After delete the user's session is invalid anyway, but explicit signOut
     * also clears the Supabase client cache so the next page load doesn't
     * briefly render the dashboard.
     */
    const handleDeleteAccount = async () => {
        if (deletePhrase !== 'DELETE MY ACCOUNT') {
            alert('Please type DELETE MY ACCOUNT exactly to confirm.');
            return;
        }
        setDeleting(true);
        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            const res = await fetch('/api/gdpr-delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken ? `Bearer ${accessToken}` : '',
                },
                body: JSON.stringify({ confirmation: 'DELETE MY ACCOUNT' }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(err.error || `Delete failed (HTTP ${res.status})`);
            }
            await supabase.auth.signOut();
            window.location.href = '/?account_deleted=1';
        } catch (err: any) {
            alert('Delete failed: ' + err.message);
            setDeleting(false);
        }
    };

    const isBasic    = user.plan === 'Basic';
    const isPro      = user.plan === 'Pro';
    const isBusiness = user.plan === 'Business';
    const isPaid     = isBasic || isPro || isBusiness;
    const limits     = getPlanLimits(user.plan);

    const usageRows = [
        { label: 'Properties', icon: Building2, used: usage.properties, limit: limits.properties },
        { label: 'Tenants',    icon: Users,     used: usage.tenants,    limit: limits.tenants    },
        { label: 'Documents',  icon: FileText,  used: usage.documents,  limit: limits.documents  },
    ];

    const planCards = [
        {
            name: 'Free', monthly: '0', annual: '0', annualTotal: null, accent: '#94a3b8',
            features: ['1 property', 'Up to 2 tenants', '3 documents', 'Maintenance tickets'],
            popular: false,
        },
        {
            name: 'Basic', monthly: '9', annual: '7', annualTotal: '84', accent: ACCENT,
            features: ['Up to 5 properties', 'Unlimited tenants', '20 documents', 'Rent reminders', 'Email support'],
            popular: false,
        },
        {
            name: 'Pro', monthly: '19', annual: '15', annualTotal: '180', accent: ACCENT,
            features: ['Up to 20 properties', '50 tenants', '100 docs + e-signing', 'Priority support', 'Reports'],
            popular: true,
        },
        {
            name: 'Business', monthly: '49', annual: '39', annualTotal: '468', accent: '#f59e0b',
            features: ['Unlimited everything', 'Full financial suite', 'API access', 'Dedicated manager'],
            popular: false,
        },
    ];

    return (
        <div className="page dashboard-page">

            {/* Upgrade success banner */}
            {justUpgraded && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', border: '1px solid #86efac', borderRadius: '18px', padding: '1.25rem 1.75rem', marginBottom: '2rem' }}>
                    <PartyPopper size={24} color={ACCENT} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, color: '#15803d' }}>Welcome to {user.plan}! 🎉</div>
                        <div style={{ fontSize: '0.875rem', color: ACCENT, fontWeight: 500, marginTop: '2px' }}>Your plan is active. All features are now unlocked.</div>
                    </div>
                    <button onClick={dismissUpgradeBanner} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, padding: '4px', borderRadius: '8px', display: 'flex' }}><X size={18} /></button>
                </div>
            )}

            {/* Admin plan switcher — dev-only bypass for whitelisted emails. */}
            {isAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #1e293b, #0f172a)', borderRadius: '18px', padding: '1rem 1.5rem', marginBottom: '1.5rem', color: '#fff', flexWrap: 'wrap' }}>
                    <ShieldCheck size={22} color="#fbbf24" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>Admin plan switcher</div>
                        <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '2px' }}>Bypass Stripe — switch your plan instantly for testing.</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {(['Free', 'Basic', 'Pro', 'Business'] as const).map(p => {
                            const active = user.plan === p;
                            return (
                                <button
                                    key={p}
                                    onClick={() => adminSetPlan(p)}
                                    disabled={adminSwitching !== null}
                                    style={{
                                        padding: '0.4rem 0.9rem', borderRadius: '10px',
                                        border: active ? '1.5px solid #fbbf24' : '1px solid rgba(148,163,184,0.3)',
                                        background: active ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.04)',
                                        color: active ? '#fbbf24' : '#e2e8f0',
                                        fontWeight: 700, fontSize: '0.8125rem',
                                        cursor: adminSwitching ? 'wait' : 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '5px',
                                    }}
                                >
                                    {adminSwitching === p ? <Loader2 size={12} className="animate-spin" /> : null}
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="page__header dashboard-page-header" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1 className="page__title" style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.04em', margin: 0 }}>Settings</h1>
                    <p className="page__subtitle" style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Manage your account and subscription</p>
                </div>
            </div>

            <div className="settings-layout">

                {/* ── Left column ─────────────────────────────────────────── */}
                <div className="settings-stack">

                    {/* Profile */}
                    <section style={card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.5rem' }}>
                            <div style={iconBox(ACCENT_LIGHT, ACCENT)}><UserCircle size={20} /></div>
                            <div>
                                <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0 }}>Profile</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#94a3b8', margin: 0 }}>Your account information</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={fieldLabel}><User size={12} /> Full Name</label>
                                <input type="text" value={nameValue} onChange={e => setNameValue(e.target.value)} style={inputBase} />
                            </div>
                            <div>
                                <label style={fieldLabel}><Mail size={12} /> Email</label>
                                <input type="email" defaultValue={user.email} disabled style={{ ...inputBase, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed' }} />
                            </div>
                            <div>
                                <label style={fieldLabel}><Globe size={12} /> Language</label>
                                <select style={{ ...inputBase, cursor: 'pointer', appearance: 'none' as const }}>
                                    <option>English</option>
                                    <option>Deutsch</option>
                                    <option>Français</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                className="btn btn-primary"
                                onClick={async () => {
                                    setSaving(true);
                                    await updateProfile({ name: nameValue });
                                    setTimeout(() => setSaving(false), 900);
                                }}
                                style={{ padding: '0.625rem 1.5rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem' }}
                            >
                                {saving ? 'Saved ✓' : 'Save Profile'}
                            </button>
                        </div>
                    </section>

                    {/* Online Payments (Stripe Connect) */}
                    <section style={card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem' }}>
                            <div style={iconBox('rgba(99,102,241,0.10)', '#6366f1')}><Banknote size={20} /></div>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0 }}>Online Rent Payments</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#94a3b8', margin: 0 }}>Optional — let tenants pay rent through the app</p>
                            </div>
                            {connect.chargesEnabled && (
                                <span style={{ fontSize: '0.65rem', fontWeight: 800, padding: '4px 10px', borderRadius: 99, background: 'rgba(22,163,74,0.12)', color: ACCENT, letterSpacing: '0.06em' }}>ACTIVE</span>
                            )}
                        </div>

                        {justConnected && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                                <CheckCircle2 size={16} color={ACCENT} />
                                <div style={{ flex: 1, fontSize: '0.8125rem', color: '#065f46', fontWeight: 600 }}>
                                    Returned from Stripe. Status refreshed below.
                                </div>
                                <button onClick={dismissConnectBanner} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', display: 'flex' }}><X size={14} /></button>
                            </div>
                        )}

                        {connect.loading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.875rem' }}>
                                <Loader2 size={14} className="animate-spin" /> Checking Stripe status…
                            </div>
                        ) : connect.chargesEnabled ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0.875rem 1rem', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', marginBottom: '0.875rem' }}>
                                    <CheckCircle2 size={18} color={ACCENT} style={{ flexShrink: 0 }} />
                                    <div style={{ flex: 1, fontSize: '0.875rem', color: '#065f46' }}>
                                        <strong style={{ fontWeight: 700 }}>Stripe account connected.</strong> Tenants can now pay rent directly to you through the app. We take no fee — funds go to your Stripe balance, then to your bank.
                                    </div>
                                </div>
                                <button
                                    onClick={refreshConnectStatus}
                                    disabled={connect.action === 'refreshing'}
                                    style={{ padding: '0.5rem 1rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.8125rem', fontWeight: 600, color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    {connect.action === 'refreshing' ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                                    Refresh status
                                </button>
                            </div>
                        ) : connect.connected && !connect.detailsSubmitted ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '0.875rem 1rem', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a', marginBottom: '0.875rem' }}>
                                    <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <div style={{ fontSize: '0.8125rem', color: '#92400e' }}>
                                        Onboarding in progress. Finish providing your details to Stripe to start accepting rent.
                                    </div>
                                </div>
                                <button
                                    onClick={handleConnectStripe}
                                    disabled={connect.action === 'onboarding'}
                                    className="btn btn-primary"
                                    style={{ padding: '0.625rem 1.25rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    {connect.action === 'onboarding' ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                                    Continue onboarding
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.875rem', lineHeight: 1.55 }}>
                                    Connect a Stripe account so tenants can pay rent online via card. Funds go straight to <strong>your</strong> Stripe balance — RentFlow never holds your money and charges no fee.
                                </div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {[
                                        'Tenants see a Pay Now button on each rent bill',
                                        'Payments auto-mark as paid — no manual tracking',
                                        'You stay in control: keep using cash/transfer if you prefer',
                                    ].map(f => (
                                        <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8125rem', color: '#64748b' }}>
                                            <CheckCircle2 size={13} color={ACCENT} style={{ flexShrink: 0 }} /> {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={handleConnectStripe}
                                    disabled={connect.action === 'onboarding'}
                                    style={{ padding: '0.625rem 1.25rem', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #635bff, #4c46d0)', color: '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(99,91,255,0.28)' }}
                                >
                                    {connect.action === 'onboarding' ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                                    Connect Stripe
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Notifications */}
                    <section style={card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem' }}>
                            <div style={iconBox('rgba(245,158,11,0.10)', '#d97706')}><Bell size={20} /></div>
                            <div>
                                <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0 }}>Notifications</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#94a3b8', margin: 0 }}>Choose what emails you receive</p>
                            </div>
                        </div>
                        {([
                            { key: 'weekly' as const,       label: 'Weekly digest',        desc: 'Summary of activity every Monday' },
                            { key: 'overdue' as const,      label: 'Overdue rent alerts',  desc: 'Instant alert when rent is late' },
                            { key: 'maintenance' as const,  label: 'Maintenance updates',  desc: 'When tenants submit new tickets' },
                        ]).map(item => (
                            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{item.label}</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '2px' }}>{item.desc}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setNotifs(n => ({ ...n, [item.key]: !n[item.key] }))}
                                    style={{ width: 44, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer', background: notifs[item.key] ? ACCENT : '#e2e8f0', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}
                                >
                                    <span style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: notifs[item.key] ? 23 : 3, boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }} />
                                </button>
                            </div>
                        ))}
                    </section>

                </div>

                {/* ── Right column: Billing ────────────────────────────────── */}
                <section style={{ ...card, display: 'flex', flexDirection: 'column' }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                            <div style={iconBox(ACCENT_LIGHT, ACCENT)}><CreditCardIcon size={20} /></div>
                            <div>
                                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>Plan & Billing</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#94a3b8', margin: 0 }}>Manage your subscription</p>
                            </div>
                        </div>
                        {isPaid && (
                            <button onClick={handleManageBilling} disabled={loadingPlan === 'portal'} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.8125rem', fontWeight: 700, color: '#475569', cursor: 'pointer' }}>
                                {loadingPlan === 'portal' ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                                Manage
                            </button>
                        )}
                    </div>

                    {/* Current plan */}
                    <div style={{ background: 'linear-gradient(135deg, #0f1c12 0%, #0a1a0d 100%)', padding: '1.75rem', borderRadius: '16px', color: '#fff', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 24px rgba(22,163,74,0.18)' }}>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.12em', color: '#4ade80', marginBottom: '6px' }}>Active Subscription</div>
                                    <div style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.04em' }}>{user.plan}</div>
                                </div>
                                <div style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.06em', background: user.subscription_status === 'past_due' ? 'rgba(251,191,36,0.25)' : user.subscription_status === 'canceled' ? 'rgba(239,68,68,0.25)' : 'rgba(74,222,128,0.18)', color: user.subscription_status === 'past_due' ? '#fbbf24' : user.subscription_status === 'canceled' ? '#f87171' : '#4ade80', border: '1px solid currentColor' }}>
                                    {user.subscription_status === 'active' ? 'ACTIVE' : user.subscription_status === 'trialing' ? 'TRIAL' : user.subscription_status === 'past_due' ? 'PAST DUE' : user.subscription_status === 'canceled' ? 'CANCELED' : isPaid ? 'ACTIVE' : 'FREE'}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {usageRows.map(row => {
                                    const pct = usagePct(row.used, row.limit);
                                    const atLimit = row.used >= row.limit;
                                    return (
                                        <div key={row.label}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><row.icon size={11} /> {row.label}</span>
                                                <span style={{ color: atLimit ? '#f87171' : 'rgba(255,255,255,0.5)' }}>{row.used} / {formatLimit(row.limit)}</span>
                                            </div>
                                            <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: atLimit ? 'linear-gradient(to right, #f87171, #ef4444)' : 'linear-gradient(to right, #16a34a, #4ade80)', borderRadius: '2px', transition: 'width 0.6s ease' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <Building2 size={110} style={{ position: 'absolute', bottom: '-18px', right: '-6px', opacity: 0.06, transform: 'rotate(-10deg)' }} />
                    </div>

                    {/* Billing cycle toggle */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                        <div style={{ display: 'inline-flex', background: '#f1f4f7', borderRadius: 99, padding: '3px', border: '1px solid #e2e5e9', gap: '2px' }}>
                            {(['monthly', 'annual'] as const).map(b => (
                                <button key={b} type="button" onClick={() => setBilling(b)} style={{ padding: '0.375rem 1rem', borderRadius: 99, border: 'none', fontSize: '0.8125rem', fontWeight: billing === b ? 700 : 500, cursor: 'pointer', background: billing === b ? '#fff' : 'transparent', color: billing === b ? '#1a1d2e' : '#94a3b8', boxShadow: billing === b ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.18s', fontFamily: 'var(--font-sans)' }}>
                                    {b === 'monthly' ? 'Monthly' : 'Yearly'}
                                    {b === 'annual' && <span style={{ marginLeft: '5px', background: ACCENT, color: '#fff', fontSize: '0.6rem', fontWeight: 800, padding: '1px 6px', borderRadius: 99 }}>-20%</span>}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Plan cards */}
                    <div className="settings-plan-grid">
                        {planCards.map(plan => {
                            const isCurrent = user.plan === plan.name;
                            const isDowngrade = (isBasic && plan.name === 'Free') || (isPro && (plan.name === 'Free' || plan.name === 'Basic')) || (isBusiness && plan.name !== 'Business');
                            const displayPrice = billing === 'annual' ? plan.annual : plan.monthly;

                            return (
                                <div key={plan.name} style={{
                                    padding: '1.25rem 1rem', borderRadius: '14px',
                                    border: isCurrent ? `2px solid ${ACCENT}` : plan.popular ? `1.5px solid rgba(22,163,74,0.3)` : '1px solid #e5e7eb',
                                    background: plan.popular && !isCurrent ? 'linear-gradient(160deg, #f0fdf4, #fff)' : '#fff',
                                    display: 'flex', flexDirection: 'column', position: 'relative',
                                    boxShadow: plan.popular ? '0 4px 20px rgba(22,163,74,0.08)' : 'none',
                                }}>
                                    {plan.popular && (
                                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)', background: ACCENT, color: '#fff', padding: '2px 12px', borderRadius: 99, fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const }}>Most Popular</div>
                                    )}
                                    <div style={{ fontWeight: 800, fontSize: '0.9375rem', marginBottom: '2px' }}>{plan.name}</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', marginBottom: billing === 'annual' && plan.annualTotal ? '2px' : '0.75rem' }}>
                                        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#94a3b8', alignSelf: 'flex-start', marginTop: '3px' }}>€</span>
                                        <span style={{ fontSize: '1.5rem', fontWeight: 900, letterSpacing: '-0.03em', color: plan.popular ? ACCENT : '#1a1d2e' }}>{displayPrice}</span>
                                        {plan.name !== 'Free' && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>/mo</span>}
                                    </div>
                                    {billing === 'annual' && plan.annualTotal && (
                                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.625rem' }}>€{plan.annualTotal} billed annually</div>
                                    )}
                                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0', display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '0.75rem', color: '#64748b', flex: 1 }}>
                                        {plan.features.map(f => (
                                            <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                                                <CheckCircle2 size={12} color={plan.accent} style={{ flexShrink: 0 }} /> {f}
                                            </li>
                                        ))}
                                    </ul>
                                    {isCurrent ? (
                                        <button disabled style={{ width: '100%', padding: '0.5rem', borderRadius: '10px', border: `1.5px solid ${ACCENT}`, background: ACCENT_LIGHT, color: ACCENT, fontWeight: 700, fontSize: '0.8125rem', cursor: 'default' }}>Current Plan</button>
                                    ) : isDowngrade ? (
                                        <button disabled style={{ width: '100%', padding: '0.5rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', fontWeight: 600, fontSize: '0.8125rem', cursor: 'not-allowed' }}>Included</button>
                                    ) : plan.name === 'Free' ? (
                                        <button disabled style={{ width: '100%', padding: '0.5rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', fontWeight: 600, fontSize: '0.8125rem' }}>Free Tier</button>
                                    ) : (
                                        <button
                                            onClick={() => handleUpgrade(plan.name)}
                                            disabled={!!loadingPlan}
                                            style={{ width: '100%', padding: '0.5rem', borderRadius: '10px', border: plan.popular ? 'none' : '1px solid #e2e8f0', background: plan.popular ? `linear-gradient(135deg, ${ACCENT}, #15803d)` : '#f8fafc', color: plan.popular ? '#fff' : '#475569', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', boxShadow: plan.popular ? '0 4px 14px rgba(22,163,74,0.25)' : 'none' } as React.CSSProperties}
                                        >
                                            {loadingPlan === plan.name ? <><Loader2 size={12} className="animate-spin" /> Processing…</> : <><Zap size={12} /> Upgrade</>}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Privacy & data (GDPR rights) */}
                    <div style={{ borderTop: '1px solid #f1f5f9', marginTop: '1.75rem', paddingTop: '1.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem' }}>
                            <div style={iconBox('#eef2ff', '#4f46e5')}><ShieldCheck size={20} /></div>
                            <div>
                                <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0 }}>Privacy & data</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#94a3b8', margin: 0 }}>Your GDPR rights — export or delete your data</p>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderRadius: '14px', border: '1px solid #e0e7ff', background: '#f8fafc' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#1e1b4b' }}>Export my data</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '2px' }}>Download a JSON of everything we have on you</div>
                                </div>
                                <button
                                    onClick={handleExportData}
                                    disabled={exportingData}
                                    style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '0.5rem 1.125rem', borderRadius: '10px', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontWeight: 700, fontSize: '0.875rem', cursor: exportingData ? 'wait' : 'pointer' }}
                                >
                                    {exportingData ? <><Loader2 size={15} className="animate-spin" /> Exporting…</> : <><FileText size={15} /> Export</>}
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff8f8', padding: '1rem 1.25rem', borderRadius: '14px', border: '1px solid #fee2e2' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#991b1b' }}>Delete my account</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#b91c1c', opacity: 0.75, marginTop: '2px' }}>Permanently erase all data. Cannot be undone.</div>
                                </div>
                                <button
                                    onClick={() => setDeleteOpen(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '0.5rem 1.125rem', borderRadius: '10px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}
                                >
                                    <AlertCircle size={15} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Security — moved here so billing takes full width */}
                    <div style={{ borderTop: '1px solid #f1f5f9', marginTop: '1.75rem', paddingTop: '1.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.25rem' }}>
                            <div style={iconBox('#fef2f2', '#dc2626')}><ShieldAlert size={20} /></div>
                            <div>
                                <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0, color: '#991b1b' }}>Security</h2>
                                <p style={{ fontSize: '0.8125rem', color: '#f87171', margin: 0 }}>Manage access and session</p>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff8f8', padding: '1rem 1.25rem', borderRadius: '14px', border: '1px solid #fee2e2' }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.9375rem' }}>Sign out</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#b91c1c', opacity: 0.75, marginTop: '2px' }}>End your current session</div>
                                </div>
                                <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '0.5rem 1.125rem', borderRadius: '10px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                                    <LogOut size={15} /> Sign out
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderRadius: '14px', border: '1px solid #f1f5f9', background: '#fafafa', cursor: 'pointer' }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Change password</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '2px' }}>Update your login credentials</div>
                                </div>
                                <ChevronRight size={16} color="#94a3b8" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Account deletion confirmation modal */}
            {deleteOpen && (
                <div
                    onClick={() => !deleting && setDeleteOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: '20px', padding: '1.875rem', maxWidth: 480, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <div style={iconBox('#fef2f2', '#dc2626')}><AlertCircle size={20} /></div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: '#0f172a' }}>Delete account</h3>
                        </div>
                        <p style={{ fontSize: '0.9375rem', color: '#334155', lineHeight: 1.6, margin: '0 0 1rem' }}>
                            This permanently erases your profile, properties, tenant links, documents, chat messages, and presence data. Your Stripe subscription will be cancelled. <strong>This cannot be undone.</strong>
                        </p>
                        <p style={{ fontSize: '0.875rem', color: '#475569', margin: '0 0 0.5rem' }}>
                            Type <code style={{ background: '#fef2f2', color: '#991b1b', padding: '0.125rem 0.5rem', borderRadius: 6, fontWeight: 700, fontSize: '0.85em' }}>DELETE MY ACCOUNT</code> to confirm:
                        </p>
                        <input
                            type="text"
                            value={deletePhrase}
                            onChange={(e) => setDeletePhrase(e.target.value)}
                            disabled={deleting}
                            placeholder="DELETE MY ACCOUNT"
                            style={{ ...inputBase, marginBottom: '1.25rem', fontFamily: 'monospace' }}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '0.625rem', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => { setDeleteOpen(false); setDeletePhrase(''); }}
                                disabled={deleting}
                                style={{ padding: '0.625rem 1.25rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '0.875rem', cursor: deleting ? 'wait' : 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteAccount}
                                disabled={deleting || deletePhrase !== 'DELETE MY ACCOUNT'}
                                style={{ padding: '0.625rem 1.25rem', borderRadius: '10px', border: 'none', background: deletePhrase === 'DELETE MY ACCOUNT' ? '#dc2626' : '#fca5a5', color: '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: (deleting || deletePhrase !== 'DELETE MY ACCOUNT') ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}
                            >
                                {deleting ? <><Loader2 size={15} className="animate-spin" /> Deleting…</> : <>Permanently delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
